import { useUpdateMeMutation } from '@entities/Authentication/rtk-query.ts';
import type { UpdateMePost } from '@entities/Authentication/types.ts';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback } from 'react';
import { doTransformRTKErrorResponse } from '@shared/helpers/index.ts';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';

export const useUpdateMe = () => {
  const [post, { isLoading }] = useUpdateMeMutation();

  const updateMe = useCallback(
    async ({
      data,
      onError,
      onSuccess,
    }: {
      data: Partial<UpdateMePost>;
      onError: ({ fieldName, message }: { fieldName?: string; message?: string }) => void;
      onSuccess: () => void;
    }) => {
      const response = await post(data);
      if (response?.error) {
        const transformedError = doTransformRTKErrorResponse(
          response?.error as FetchBaseQueryError
        );
        onError &&
          onError({
            fieldName: transformedError.firstErrorFiledName,
            message: transformedError.firstError,
          });
      } else {
        onSuccess && onSuccess();
      }
    },
    [post]
  );

  return {
    isLoading,
    updateMe,
  };
};

export const usePersonalRedirect = () => {
  const { doRedirectWithSavePageUrl, doRedirectOnPrevPage, doSavePageUrl, prevPageUrl } =
    usePagesRedirect('personalRoute');

  const redirectOnPersonalPageWithSaveUrl = useCallback(() => {
    doRedirectWithSavePageUrl('/personal');
  }, [doRedirectWithSavePageUrl]);

  return {
    redirectOnPersonalPageWithSaveUrl,
    savePersonalPrevPageUrl: doSavePageUrl,
    doRedirectOnPrevPage,
    prevPageUrl,
  };
};
